import {
  MuiModal,
  Box,
  Flex,
  Text,
  InputV2,
  SwitchV2,
  Button,
} from '@pinchenterprisesnpm/friday-ui'
import { useMemo } from 'react'
import { ShiftPostPage } from '@src/features/shift-post-v2/pages'
import { IndividualDaysList } from '@src/features/shift-post-v2/steps/shift-details-step/components/schedule-section/components/individual-days-list/indivitual-days-list'
import { ShiftPostStep } from '@src/features/shift-post-v2/steps/steps.types'
import { useSelector } from 'react-redux'
import { selectBusinessEntity } from '@src/data/BusinessEntity/selectors'
import { connect } from 'formik'
import { SHIFT_TYPE } from '@src/models/Shift'
import { MultiSingleDayPicker } from '@src/features/shift-post-v2/steps/shift-details-step/components/schedule-section/components/multi-single-day-picker/multi-single-day-picker'
import { SingleDayPicker } from '@src/features/shift-post-v2/steps/shift-details-step/components/schedule-section/components/single-day-picker/single-day-picker'
import { RecurringDayPicker } from '@features/shift-post-v2/components'
import { CustomTimePicker } from '@src/features/shift-post-v2/steps/shift-details-step/components/schedule-section/components/custom-time-picker'
import CustomDropdownV5 from '@src/features/shift-post-v2/steps/shift-details-step/components/schedule-section/components/custom-dropdown-v5/custom-dropdown-v5'
import {
  handlePayRateChange,
  handleHourChange,
  handleBreakChange,
} from '@src/features/shift-post-v2/steps/shift-details-step/components/schedule-section/form-handlers'
import { faAlarmClock } from '@fortawesome/pro-solid-svg-icons'
import { faDollarSign } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { getBreakValue } from '@src/ui/components/Admin/Shifts/ShiftEditDuplicate/utils'
import { IScheduleSectionFormValues } from '@src/features/shift-post-v2/steps/shift-details-step/components/schedule-section/schedule-section.types'
import { FormikProps } from 'formik'
import { useDatesSelectedOnModalOpenRef } from './useDatesSelectedOnModalOpenRef'

const { Header, Content, Footer } = MuiModal

interface IEditScheduleModalProps {
  isOpen: boolean
  closeModal: () => void
  sourcePage: ShiftPostPage
  formik?: FormikProps<IScheduleSectionFormValues>
}

export const EditScheduleModal = connect<IEditScheduleModalProps>(
  ({ isOpen, closeModal, sourcePage, formik }) => {
    const businessEntity = useSelector(selectBusinessEntity)
    const { values, setFieldValue, handleBlur, errors } = formik
    const hasDatesSelected = useMemo(
      () =>
        values.type === SHIFT_TYPE.SINGLEDAY
          ? !!(values.single_day_shifts && values.single_day_shifts.length > 0)
          : !!(values.days && values.days.length > 0),
      [values.days, values.single_day_shifts, values.type]
    )

    const { hasDatesSelectedOnModalOpenRef } = useDatesSelectedOnModalOpenRef({
      hasDatesSelected,
      isOpen,
    })

    // Determine if we should show the full schedule section or just date selection
    const shouldShowFullSchedule = () => {
      // For copy-shift in Confirm step with no dates selected, show full schedule
      if (sourcePage === ShiftPostPage.COPY_SHIFT) {
        const hasDatesOnModalOpen = hasDatesSelectedOnModalOpenRef.current ?? hasDatesSelected
        return !hasDatesOnModalOpen
      }

      // For edit-shift, only show date selection for single day, show individual days for multiday
      if (sourcePage === ShiftPostPage.EDIT_SHIFT) {
        return false
      }

      // For post-shift, show full schedule
      return sourcePage === ShiftPostPage.POST_SHIFT
    }

    // Determine if we should show date selection or just individual days
    const shouldShowDateSelection = () => {
      // For edit-shift with multiday, don't show date selection (only show individual days)
      if (sourcePage === ShiftPostPage.EDIT_SHIFT && values.type === SHIFT_TYPE.MULTIDAY) {
        return false
      }

      return true
    }

    const showFullSchedule = shouldShowFullSchedule()
    const showDateSelection = shouldShowDateSelection()

    return (
      <MuiModal isOpen={isOpen} closeModal={closeModal} fullWidth maxWidth='md'>
        <Header title='Edit Schedule' />

        <Content>
          <Box>
            {showFullSchedule ? (
              // Full schedule section (for copy-shift with no dates or post-shift)
              <>
                {/* Recurring Shift Toggle */}
                <Flex alignItems='center' mb='md'>
                  <Text mr='sm' color='gray'>
                    Recurring Shift
                  </Text>

                  <SwitchV2
                    value={values.is_recurring_shift}
                    onChange={(value: boolean) => {
                      setFieldValue('is_recurring_shift', value)
                      setFieldValue('type', value ? SHIFT_TYPE.MULTIDAY : SHIFT_TYPE.SINGLEDAY)
                    }}
                  />
                </Flex>

                {/* Date picker */}
                <Flex mt='md'>
                  <Box width={values.type === SHIFT_TYPE.MULTIDAY ? 'unset' : '100%'}>
                    {values.type === SHIFT_TYPE.SINGLEDAY &&
                      [ShiftPostPage.POST_SHIFT, ShiftPostPage.COPY_SHIFT].includes(sourcePage) && (
                        <MultiSingleDayPicker formik={formik} isDisabled={false} />
                      )}
                    {values.type === SHIFT_TYPE.SINGLEDAY &&
                      sourcePage === ShiftPostPage.EDIT_SHIFT && (
                        <SingleDayPicker formik={formik} isDisabled={false} />
                      )}
                    {values.type === SHIFT_TYPE.MULTIDAY && (
                      <RecurringDayPicker formik={formik as any} isDisabled={false} />
                    )}
                  </Box>
                </Flex>

                {/* Time picker */}
                <Flex mt='md'>
                  <Box mr='md' width='50%'>
                    <CustomTimePicker
                      value={values.hour_start}
                      onChange={(value) => handleHourChange(value, true, formik)}
                      label='Start Time'
                      hasError={!!errors.hour_start}
                      width='100%'
                      isDisabled={false}
                    />
                  </Box>

                  <Box width='50%'>
                    <CustomTimePicker
                      value={values.hour_end}
                      label='End Time'
                      width='100%'
                      hasError={!!errors.hour_end}
                      onChange={(value) => handleHourChange(value, false, formik)}
                      isDisabled={false}
                    />
                  </Box>
                </Flex>

                {/* Pay rate and unpaid break */}
                <Flex mt='md'>
                  <Flex mr='md' width='50%'>
                    <InputV2
                      leftElement={
                        <FontAwesomeIcon
                          icon={faDollarSign}
                          size='lg'
                          color='#C5C5C7'
                          style={{
                            height: 24,
                            width: 24,
                          }}
                        />
                      }
                      labelStyles={{
                        color: '#868686',
                        fontWeight: 400,
                      }}
                      leftElementStyles={{
                        top: 8,
                        left: -1,
                      }}
                      numberArrowIconColor='gray.400'
                      numberArrowsWrapperStyles={{
                        right: 3,
                      }}
                      label='Hourly Pay Rate'
                      placeholder=''
                      type='number'
                      name='pay_value'
                      id='pay_value'
                      width='100%'
                      value={values.pay_value}
                      isDisabled={false}
                      onChange={(e) => handlePayRateChange(e, formik)}
                      onBlur={handleBlur}
                      errorMessage={errors.pay_value}
                      onKeyDown={(e) => {
                        if (['e', 'E', '-', '+'].includes(e.key)) {
                          e.preventDefault()
                        }
                      }}
                    />
                  </Flex>

                  <Box height='50px' width='50%'>
                    <CustomDropdownV5
                      topLabel='Unpaid break'
                      icon={faAlarmClock}
                      options={getBreakValue()}
                      value={
                        getBreakValue().find((opt) => opt.value === (values.break || 0)) ||
                        getBreakValue()[0]
                      }
                      onChange={(selected: any) => handleBreakChange(selected, formik)}
                      isDisabled={false}
                      width='100%'
                    />
                  </Box>
                </Flex>
              </>
            ) : showDateSelection ? (
              // Date selection only (for edit-shift single day or copy-shift with existing dates)
              <Box>
                <Text mb='md' fontWeight='normal'>
                  Select dates for your shift:
                </Text>

                {values.type === SHIFT_TYPE.SINGLEDAY &&
                  [ShiftPostPage.POST_SHIFT, ShiftPostPage.COPY_SHIFT].includes(sourcePage) && (
                    <MultiSingleDayPicker formik={formik} isDisabled={false} />
                  )}

                {values.type === SHIFT_TYPE.SINGLEDAY &&
                  sourcePage === ShiftPostPage.EDIT_SHIFT && (
                    <SingleDayPicker formik={formik} isDisabled={false} />
                  )}

                {values.type === SHIFT_TYPE.MULTIDAY && (
                  <RecurringDayPicker formik={formik as any} isDisabled={false} />
                )}
              </Box>
            ) : null}

            {/* Individual days list - always show */}
            <Box mt='md'>
              <IndividualDaysList
                sourcePage={sourcePage}
                sourceStep={ShiftPostStep.CONFIRM}
                businessEntity={businessEntity}
              />
            </Box>
          </Box>
        </Content>
        <Footer>
          <Button onClick={closeModal}>OK</Button>
        </Footer>
      </MuiModal>
    )
  }
)
